import React, { useState, useEffect, useRef, useContext } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faMagnifyingGlass, faSave, faFilter } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useFetcher } from "@remix-run/react";
import axios from 'axios';
import { useOutletContext } from "@remix-run/react";
import { captureException } from "~/utilities/sentry.utility";
import Button from './Button';

const SaveScreenModal: React.FC<{ isOpen: boolean, onClose: (isOpen: boolean) => void, displayData: any, userId: any }> = ({ isOpen, onClose, displayData, userId }) => {

    const fetcher = useFetcher();
    const [suggestions, setSuggestions] = useState<Array<{
        ticker_symbol: string;
        name: string;
        market_cap: number;
        logo_url: string;
        ticker_id: string;
        exchange: string;
        company_id: string;
    }>>([]);
    const inputRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(-1);

    const [isFocused, setIsFocused] = useState(false);

    const [screenerName, setScreenerName] = useState('');
    const [expandedFilters, setExpandedFilters] = useState<{ [key: number]: boolean }>({});

    const [error, setError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setScreenerName(event.target.value);
    };

    useEffect(() => {
        if (isOpen) {
            // Function to focus the input using the element ID
            const focusInputById = () => {
                const inputElement = document.getElementById('screenerName') as HTMLInputElement;
                if (inputElement) {
                    inputElement.focus();
                }
            };

            // Immediate focus attempt
            focusInputById();

            // Retry focusing after a short delay to handle any asynchronous UI updates
            const timer = setTimeout(focusInputById, 300);

            return () => {
                clearTimeout(timer);
            };
        }
    }, [isOpen]);

    const handleSave = async () => {
        if (!screenerName.trim()) {
            setError(true);
            return;
        }
        setError(false);
        setIsLoading(true);

        try {
            const response = await axios.post('/api/save/company-screener', {
                data: {
                    name: screenerName,
                    filters: displayData,
                    userId: userId
                }
            });

            if (response.data.success) {
                onClose(false); // Close modal on success
                // Optional: Add success notification
            }
        } catch (error) {
            console.error('Error saving screener:', error);
            captureException(error);
            // Optional: Add error notification
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="search-modal">
            <Dialog open={isOpen} onClose={() => { }} className="relative z-50">
                <DialogBackdrop
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
                />

                <div className="fixed inset-0 w-screen overflow-y-auto pt-20">
                    <div className="flex min-h-full items-start justify-center p-4 text-center sm:items-start sm:p-0">
                        <DialogPanel
                            className="relative transform rounded-2xl bg-white px-6 pb-6 pt-6 text-left shadow-2xl transition-all duration-300 sm:my-8 w-[95%] sm:w-full sm:max-w-md border border-gray-100"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900">Save Screener</h3>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => onClose(false)}
                                    className="rounded-full p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                >
                                    <FontAwesomeIcon icon={faCircleXmark} className="text-2xl text-black" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="space-y-6">
                                {/* Screener Name Input */}
                                <div>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            className={`w-full px-4 py-3 text-black border rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-green-700 focus:border-green-700 text-sm transition-all duration-200 ${error
                                                    ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500'
                                                    : 'border-gray-200 hover:border-gray-300 focus:border-green-700'
                                                }`}
                                            id="screenerName"
                                            value={screenerName}
                                            onChange={(e) => {
                                                setError(false);
                                                handleNameChange(e);
                                            }}
                                            required
                                            autoComplete='off'
                                            onFocus={() => setIsFocused(true)}
                                            ref={inputRef}
                                            placeholder="Enter screener name"
                                        />
                                        {error && (
                                            <div className="flex items-center gap-2 mt-2 text-sm text-red-600">
                                                Please enter a screener name
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Filters Preview */}
                                {displayData && displayData.length > 0 && (
                                    <div>
                                        <div className="flex items-center gap-2 mb-3">
                                            <FontAwesomeIcon icon={faFilter} className="text-gray-400 text-sm" />
                                            <span className="text-xs md:text-sm font-semibold text-gray-700">Active Filters</span>
                                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                                {displayData.length}
                                            </span>
                                        </div>

                                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                                            {displayData.length > 3 ? (
                                                <div className="space-y-2">
                                                    <div className="flex flex-wrap gap-2">
                                                        {displayData.slice(0, expandedFilters[0] ? displayData.length : 3).map((filter: any, index: any) => (
                                                            <span key={index} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 rounded-lg border border-green-200">
                                                                {filter}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    {!expandedFilters[0] && displayData.length > 3 && (
                                                        <button
                                                            onClick={() => setExpandedFilters(prev => ({ ...prev, [0]: true }))}
                                                            className="text-xs font-medium text-green-600 hover:text-green-700 underline transition-colors duration-200"
                                                        >
                                                            Show {displayData.length - 3} more filters
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="flex flex-wrap gap-2">
                                                    {displayData.map((data: any, index: number) => (
                                                        <span key={index} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 rounded-lg border border-green-200">
                                                            {data}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Save Button */}
                                <div className="pt-2 flex justify-center">
                                    <Button
                                        type="primary"
                                        size="medium"
                                        onClick={handleSave}
                                        disabled={isLoading}
                                    >

                                        {isLoading ? (
                                            <div className="flex items-center gap-2">
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                Saving...
                                            </div>
                                        ) : (
                                            'Save Screener'
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default SaveScreenModal;
